import dayjs, { ConfigType } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { isNaN } from 'lodash';

import { Milliseconds } from '../types';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const MILLISECONDS_IN_A_SECOND: Milliseconds = 1_000;
export const MILLISECONDS_IN_A_MINUTE: Milliseconds = 60_000;

/**
 * Displays the duration between two times.  Useful for determining the total time during which
 * the data was collected (MAIR is on the order of 2+ hours, MSAT is on the order of ~30 seconds).
 * @example
 * duration('2022-01-01T20:00:00Z', '2022-01-01T20:00:30Z');
 * // -> '30 seconds'
 */
export const duration = (timeStart: ConfigType, timeEnd: ConfigType) => {
    const diffMilliseconds: Milliseconds = dayjs(timeEnd).diff(timeStart);
    const isLessThanAMinute = diffMilliseconds < MILLISECONDS_IN_A_MINUTE;
    if (isLessThanAMinute) {
        /**
         * Special handling needed here since dayjs will output "a few seconds" for everything
         * less than 45 seconds, which is too coarse.
         * See https://day.js.org/docs/en/display/from-now
         */
        return `${millisecondsToSeconds(diffMilliseconds)} seconds`;
    } else {
        return dayjs(timeEnd).from(dayjs(timeStart), true);
    }
};

/**
 * Formats measurement time for display.  UTC time (Zulu time) is preferable to reduce general
 * confusion.  When optional timeEnd is given, the time is formatted as a range.
 * @example
 * formatMeasurementTime('2022-11-01T20:03:20Z');
 * // -> 'Nov 1, 2022 8pm UTC'
 * formatMeasurementTime('2022-11-01T20:03:20Z', '2022-11-01T22:03:20Z');
 * // -> 'Nov 1, 2022 8-10pm UTC'
 */
export const formatMeasurementTime = (time: ConfigType, timeEnd?: ConfigType) => {
    const hour1 = dayjs.utc(time).format('h');
    const hour2 = dayjs.utc(timeEnd).format('h');

    /** Handling for AM->PM, PM->AM. */
    const timePeriod1 = dayjs.utc(time).format(':mma');
    const timePeriod2 = dayjs.utc(timeEnd).format(':mma');

    const showRange = typeof timeEnd !== 'undefined' && hour1 !== hour2;
    const start = `${hour1}${timePeriod1}`;
    const end = `${hour2}${timePeriod2}`;

    return showRange
        ? dayjs.utc(time).format(`MMM D, YYYY [${start}]-[${end}] UTC`)
        : dayjs.utc(time).format('MMM D, YYYY h:mma UTC');
};

/**
 * Returns the input date with UTC hours 23:59:59.999
 */
export const getDayEnd = (inputDate: Date) => {
    const copiedDate = new Date(inputDate.getTime());
    copiedDate.setUTCHours(23, 59, 59, 999);
    return copiedDate;
};

export const getIsValidDateString = (input: unknown) => {
    if (typeof input !== 'string') return false;
    const testDate = new Date(input);
    return !isNaN(testDate.getTime());
};

/**
 * Parses date string to UTC Date using dayjs. Produces valid dates across supported browsers (Safari, Chrome, and Firefox).
 */
export const parseDateString = (dateString: string) => {
    const formats = ['YYYY-MM-DD', 'YYYY-M-DD', 'YYYY-MM-D', 'YYYY-M-D'];

    // setting strict to true makes sure we don't silently roll over dates like 2023-02-31 to 2023-03-03
    const parsedDates = formats.map((format) => dayjs.utc(dateString, format, true));
    const validDate = parsedDates.find((date) => date.isValid());

    if (validDate) return validDate.toDate();

    return null;
};

/** tests whether or not the input can produce a valid object */
export const getMakesValidDate = (input: unknown) => {
    if (
        !input ||
        Array.isArray(input) ||
        (typeof input !== 'string' && typeof input !== 'number' && typeof input !== 'object')
    )
        return false;

    let testDate;

    try {
        if (typeof input === 'string') {
            testDate = parseDateString(input);
        } else {
            testDate = new Date(input as number | Date);
        }
    } catch {
        return false;
    }
    return !!testDate && !isNaN(testDate.getTime());
};

/** tests whether the input is */
export const isDate = (input: unknown): input is Date => {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return false;
    let _testDate;
    try {
        _testDate = new Date(input as Date);
    } catch {
        return false;
    }
    return true;
};

/**
 * Converts milliseconds to seconds.
 * @example
 * millisecondsToSeconds(1234);
 * // -> 1
 */
export const millisecondsToSeconds = (milliseconds: Milliseconds) =>
    Math.round(milliseconds / MILLISECONDS_IN_A_SECOND);

/**
 * Displays the relative amount of time that's passed.  Useful for giving users a sense of the
 * freshness of the data.
 * @example
 * timeFromNow('2022-01-01T20:03:20Z');
 * // -> 'a year ago'
 */
export const timeFromNow = (timeInPast: ConfigType, referenceTime: ConfigType = Date.now()) =>
    dayjs.utc(referenceTime).to(dayjs.utc(timeInPast));
